import type { MaybeRef } from 'vue'

export * from './peaks'

function clamp(min: number, value: number, max: number) {
  return (value < min) ? min : (value > max) ? max : value
}

export function useDragger(targetRef: MaybeRef<HTMLElement | undefined>, update: (value: number) => void) {
  let isDragging = false
  let isDestroyed = false

  function handleDragMove(event: MouseEvent | TouchEvent) {
    const target = toValue(targetRef)

    if (!isDragging || isDestroyed)
      return

    if (!target)
      throw new Error('target or targetRef is null')

    const { pageX } = event instanceof MouseEvent ? event : event.touches[0]
    const rect = target.getBoundingClientRect()

    const width = target.clientWidth
    const offsetX = Math.max(pageX - rect.x, 0)

    const x = clamp(0, offsetX / width, 1)
    update(x)
  }

  function handleDragStart(event: MouseEvent | TouchEvent) {
    isDragging = true
    handleDragMove(event)
  }

  function handleDragEnd() {
    isDragging = false
  }

  const config = { passive: true }

  useEventListener(targetRef, ['mousedown', 'touchstart'], handleDragStart, config)
  useEventListener(window, ['mouseup', 'touchend'], handleDragEnd, config)
  useEventListener(window, ['mousemove', 'touchmove'], handleDragMove, config)
  onBeforeUnmount(() => isDestroyed = true)

  return {
    get isActive() {
      return isDragging
    }
  }
}

export function useAudioPlayback(src: string, {
  /** Wrapper for peaks element */
  peaksRef = ref() as MaybeRef<HTMLElement | undefined>,
  audioRef = ref(new Audio(src)) as MaybeRef<HTMLAudioElement>
} = {}) {
  const dragger = useDragger(peaksRef, onDrag)

  const state = reactive({
    time: 0,
    progress: 0,
    duration: 0,

    isLoading: true,
    isPlaying: false,
    hasError: false
  })

  function getAudio() {
    const audio = toValue(audioRef)

    if (!audio)
      throw new Error('Could not get audio element from audioRef')

    return audio
  }

  function updateSeekbar() {
    const node = toValue(peaksRef)

    // Update player dom progress
    if (node)
      node.style.setProperty('--progress', `${state.progress || 0}%`)
  }

  function onDrag(value: number) {
    const audio = getAudio()
    const duration = audio.duration || 0

    if (!duration && state.isLoading)
      return Logger.warn('Ignored onDrag() call while audio is loading.')

    state.progress = value * 100
    state.time = Math.round(value * duration)
    audio.currentTime = value * duration

    updateSeekbar()
  }

  function onTimeUpdate() {
    if (dragger.isActive)
      return

    const audio = getAudio()
    state.progress = (audio.currentTime / audio.duration) * 100
    state.time = Math.round(audio.currentTime)

    updateSeekbar()
  }

  function onPlayChange() {
    state.isPlaying = !getAudio().paused
  }

  function onMetadataLoaded() {
    state.duration = getAudio().duration
  }

  async function onError(error?: Error) {
    const audio = getAudio()
    const isBlobUrl = audio.src.startsWith('blob:')

    const context = {
      'Url': audio.src,
      'Page Url': location.href,
      'Error': { name: error?.name, message: error?.message },
      'Media Error': {
        code: audio.error?.code,
        key: Object.keys(MediaError).find(key => (MediaError as any)[key] === audio.error?.code),
        message: audio.error?.message
      }
    }

    Logger.warn('AudioPlayer: Error occurred', { error, context })

    if (audio.error) {
      const { code } = audio.error
      const isCodecError = code === MediaError.MEDIA_ERR_DECODE || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED

      // Request re-encode on codec failure
      if (!isBlobUrl && isCodecError) {
        error = new Error('Audio codec issue', { cause: error })

        ErrorService.logMessage('AudioPlayer: Requesting re-encode for audio', { extra: context })
        api.audio.requestEncode(src)
      }
    }

    // Skip for minor errors
    if (error) {
      const nonErrors = [
        'can only be initiated by a user gesture'
      ]

      if (nonErrors.includes(error.message))
        return ErrorService.logMessage(error.message)
    }

    ErrorService.log(error, {
      title: 'Failed to play audio',
      extras: { context }
    })

    track('Audio load error', context)
    state.hasError = true
  }

  async function togglePlay() {
    const audio = getAudio()
    const method = audio.paused ? 'play' : 'pause'

    try {
      track('Click: toggle audio player', { method })
      await audio[method]()
    }
    catch (cause) {
      onError(
        new Error(`Failed to invoke audio method ${method}`, { cause })
      )
    }
  }

  /** On audio first load */
  function onLoad() {
    const audio = getAudio()

    // Set source if not defined
    // ! Setting same source can cause abort error
    if (audio.src !== src)
      audio.src = src
  }

  /** Resets audio player */
  function reset() {
    state.time = 0
    state.progress = 0
    getAudio().currentTime = 0
    updateSeekbar()
  }

  /** Cleanup audio player */
  function flush() {
    const audio = getAudio()

    try {
      audio.pause()
      audio.srcObject = null
      audio.src = ''
      audio.remove()
    }
    catch (error) {
      console.error('Failed to flush audio element\n\n', error)
    }
  }

  // Check for state changes
  useEventListener(audioRef, ['play', 'pause'], onPlayChange)
  useEventListener(audioRef, 'timeupdate', onTimeUpdate)

  // Check for loading state
  useEventListener(audioRef, ['loadstart', 'loadedmetadata', 'loadeddata', 'load', 'waiting', 'canplay', 'canplaythrough'], (event) => {
    const { readyState } = getAudio()
    const canPlay = readyState >= HTMLMediaElement.HAVE_METADATA
    state.isLoading = !canPlay

    if (canPlay)
      state.hasError = false

    // Update duration on metadata load
    if (event.type === 'loadedmetadata')
      onMetadataLoaded()

    console.info('AudioPlayer: state changed', { event, canPlay, readyState })
  })

  // Handle errors
  useEventListener(audioRef, ['abort', 'error'], (event) => {
    event.preventDefault()
    onError()
    return true
  })

  onBeforeUnmount(flush)
  onMounted(onLoad)

  return {
    peaksRef,
    audioRef,

    state,
    togglePlay,
    reset,
    flush
  }
}
